import {
  AuthenticationRestControllerService,
  ConfirmPasswordResetRequest,
  ConfirmUserRequest,
  LoginRequest,
  SchoolGroupControllerService,
  SchoolGroupLoginRequest,
} from 'generated';
import localforage from 'localforage';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ACCESS_TOKEN, REFRESH_TOKEN, USER } from 'utils/constants';
import { apiWrapper } from 'utils/http-client';
import { handleError } from 'utils/utils';
import useGroupURL from './useGroupURL';

import { Notify } from '@flexisaf/flexibull2';

const useAuthentication = () => {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [profile, setProfile] = useState<any>({});

  const navigate = useNavigate();
  const { groupURL } = useGroupURL();

  const postLogin = useCallback(
    async (payload: SchoolGroupLoginRequest) => {
      setLoading(true);
      try {
        const response = await apiWrapper(() =>
          SchoolGroupControllerService.loginUsingPost1({
            request: payload,
          }),
        );
        const access_token = response?.ums_login_response?.access_token;
        const refresh_token = response?.ums_login_response?.refresh_token;
        const profile = response?.user;

        localforage.setItem(ACCESS_TOKEN, access_token);
        localforage.setItem(REFRESH_TOKEN, refresh_token);
        localforage.setItem(USER, profile);

        setLoading(false);
        navigate(`/${groupURL}/dashboard`);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    },
    [groupURL, navigate],
  );

  const postVerifyToken = useCallback(async (confirmUserRequest: ConfirmUserRequest) => {
    setLoading(true);
    try {
      const response = await apiWrapper(() =>
        AuthenticationRestControllerService.confirmEmisUserUsingPost({
          confirmUserRequest,
        }),
      );
      if (response.token) {
        setToken(response.token);
        // setHasVerified(true);
      } else {
        // setHasVerified(false);
        Notify(
          `An error occured while verifying your account. 
          Refresh the page to try again. Please contact support if issue persists.`,
          { status: 'error' },
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setToken('');
      handleError(error);
    }
  }, []);

  const postResetPassword = useCallback(
    async (confirmPasswordResetRequest: ConfirmPasswordResetRequest) => {
      setLoading(true);
      try {
        const response = await apiWrapper(() =>
          AuthenticationRestControllerService.confirmEmisPasswordResetRequestUsingPost({
            confirmPasswordResetRequest,
          }),
        );
        Notify('Your new password has been saved. Log into your account.');
        navigate(`/${groupURL}`);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    },
    [groupURL, navigate],
  );

  const logOut = useCallback(async () => {
    try {
      await localforage.clear();
      navigate(`/${groupURL}`);
    } catch (error) {}
  }, [groupURL, navigate]);

  const initiateUserProfile = useCallback(async () => {
    const userProfile = await localforage.getItem(USER);
    setProfile(userProfile);
  }, []);

  useEffect(() => {
    initiateUserProfile();
  }, [initiateUserProfile]);

  return {
    postLogin,
    loading,
    postVerifyToken,
    postResetPassword,
    token,
    logOut,
    profile,
  };
};

export default useAuthentication;
